import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import ScrollHint from '@/Screens/Dashboard/component/LeaderboardPreview/ScrollHint';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {handleLogout} from '@/Utils/CommonUtils';
import useCsocPlayerIdentity from '@/Hooks/RenewalApi/useCsocPlayerIdentity';

// Any gap this small between scroll position and content end still counts
// as "at the bottom" -- avoids the hint lingering over sub-pixel rounding.
const SCROLL_BOTTOM_THRESHOLD = 4;
// Matches styles.expandedList's maxHeight -- known statically, so the
// "does this even overflow" check doesn't need an onLayout round-trip.
const EXPANDED_LIST_MAX_HEIGHT = 280;

export interface ResolvedCsocIdentity {
  player_id?: number;
  player_name?: string;
  email?: string;
  dob?: string;
  gender?: string;
}

interface CsocPlayerIdentityStepProps {
  mobileNumber: string;
  onResolved: (identity: ResolvedCsocIdentity) => void;
  onUnresolved: () => void;
}

// Matches the entry-fee radio-card style (CCTournaments/EntryFeeSelect.tsx)
// so this step's player picker doesn't look like a different product.
const MAX_INLINE_PLAYERS = 5;

interface PlayerOptionRowProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}

const PlayerOptionRow: React.FC<PlayerOptionRowProps> = ({
  label,
  selected,
  onPress,
  testID,
}) => (
  <TouchableOpacity
    style={[styles.optRow, selected && styles.optRowSelected]}
    onPress={onPress}
    activeOpacity={0.7}
    testID={testID}>
    <View style={[styles.radioOuter, selected && styles.radioOuterSelected]}>
      {selected && <View style={styles.radioDot} />}
    </View>
    <CCText fontSize={14} fontWeight="semibold" color="#262322">
      {label}
    </CCText>
  </TouchableOpacity>
);

const sortByName = <T extends {player_name: string}>(players: T[]): T[] =>
  [...players].sort((a, b) =>
    a.player_name.localeCompare(b.player_name, undefined, {
      sensitivity: 'base',
    }),
  );

const CsocPlayerIdentityStep: React.FC<CsocPlayerIdentityStepProps> = ({
  mobileNumber,
  onResolved,
  onUnresolved,
}) => {
  const {mutate: lookupIdentity, isLoading, isSuccess, isError, data} =
    useCsocPlayerIdentity();

  useEffect(() => {
    lookupIdentity(mobileNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNumber]);

  const [notThemConfirmed, setNotThemConfirmed] = useState(false);
  const [pickedPlayerId, setPickedPlayerId] = useState<number | null>(null);
  const [showFullForm, setShowFullForm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const expandedListRef = useRef<ScrollView>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');

  const handleExpandedListScroll = (
    e: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    const {contentOffset, contentSize, layoutMeasurement} = e.nativeEvent;
    const distanceFromBottom =
      contentSize.height - layoutMeasurement.height - contentOffset.y;
    setScrolledToBottom(distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD);
  };

  // Fires once content lays out. If the list doesn't even fill the box
  // there's nothing to scroll to -- start with the hint already hidden
  // instead of waiting for a scroll event that will never come.
  const handleExpandedListContentSizeChange = (
    _width: number,
    contentHeight: number,
  ) => {
    setScrolledToBottom(contentHeight <= EXPANDED_LIST_MAX_HEIGHT);
  };

  const effectiveState = isError ? 'fresh' : data?.state;

  // Single source of truth for "are we showing/using the full name/email/dob/
  // gender form", shared by the render fallthrough below and the effect that
  // resolves/unresolves based on its fields. Must stay in sync with the JSX
  // branches above the final return: 'attached' never falls through, and
  // 'pick_one' only falls through once the user has picked "Someone else"
  // (showFullForm). Any other effectiveState (including an unexpected/
  // unrecognized value) falls through to the full form, same as the render.
  const usingFullForm =
    effectiveState !== 'attached' &&
    (effectiveState !== 'pick_one' || showFullForm);

  useEffect(() => {
    if (effectiveState === 'attached' && !notThemConfirmed) {
      if (data?.attached_player?.player_id) {
        onResolved({player_id: data.attached_player.player_id});
      } else {
        // Contract violation from the backend (attached state without a
        // real attached_player/player_id) -- don't silently resolve with a
        // garbage/undefined identity.
        onUnresolved();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveState, notThemConfirmed]);

  useEffect(() => {
    if (pickedPlayerId != null) {
      onResolved({player_id: pickedPlayerId});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedPlayerId]);

  useEffect(() => {
    if (!usingFullForm) {
      return;
    }
    if (name.trim() && email.trim()) {
      onResolved({
        player_name: name.trim(),
        email: email.trim(),
        ...(dob.trim() ? {dob: dob.trim()} : {}),
        ...(gender.trim() ? {gender: gender.trim()} : {}),
      });
    } else {
      onUnresolved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usingFullForm, name, email, dob, gender]);

  if (isLoading || (!isSuccess && !isError)) {
    return null;
  }

  if (effectiveState === 'attached' && !notThemConfirmed) {
    return (
      <Box padding={12}>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
          Enrolling
        </CCText>
        <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark}>
          {data?.attached_player?.player_name}
        </CCText>
        <CCText
          fontSize={FontSizes[12]}
          color={CCColors.Primary.Default}
          onPress={() => {
            setNotThemConfirmed(true);
            onUnresolved();
          }}
          style={{textDecorationLine: 'underline', marginTop: 6, cursor: 'pointer'} as any}>
          Not you?
        </CCText>
      </Box>
    );
  }

  if (effectiveState === 'attached' && notThemConfirmed) {
    return (
      <Box padding={12} gap={8}>
        <CCText fontSize={FontSizes[13]} color={CCColors.TextColor.Dark}>
          This number is already linked to {data?.attached_player?.player_name}.
        </CCText>
        <CCButton
          testID="csoc-identity-logout-btn"
          onPress={() =>
            handleLogout(() => {
              window.location.href = '/login';
            })
          }>
          <CCText fontSize={FontSizes[13]} fontWeight="bold" color={CCColors.White}>
            Log out
          </CCText>
        </CCButton>
        <CCButton
          testID="csoc-identity-start-over-btn"
          onPress={() => {
            setNotThemConfirmed(false);
            onUnresolved();
          }}>
          <CCText fontSize={FontSizes[13]} fontWeight="bold" color={CCColors.TextColor.Dark}>
            Go back &amp; start over
          </CCText>
        </CCButton>
      </Box>
    );
  }

  if (effectiveState === 'pick_one' && !showFullForm) {
    const sortedPlayers = sortByName(data?.players || []);
    const overflowCount = sortedPlayers.length - MAX_INLINE_PLAYERS;
    const visiblePlayers = expanded
      ? sortedPlayers
      : sortedPlayers.slice(0, MAX_INLINE_PLAYERS);

    const playerRows = visiblePlayers.map(player => (
      <PlayerOptionRow
        key={player.player_id}
        label={player.player_name}
        selected={pickedPlayerId === player.player_id}
        onPress={() => setPickedPlayerId(player.player_id)}
      />
    ));

    return (
      <Box padding={12} gap={8}>
        <CCText
          fontSize={FontSizes[16]}
          fontWeight="bold"
          color={CCColors.TextColor.Dark}
          marginBottom={4}>
          Who&apos;s joining CircleChess?
        </CCText>
        {expanded ? (
          <View style={styles.expandedListWrap}>
            <ScrollView
              ref={expandedListRef}
              style={styles.expandedList}
              onScroll={handleExpandedListScroll}
              onContentSizeChange={handleExpandedListContentSizeChange}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={false}>
              <View style={{gap: 8}}>{playerRows}</View>
            </ScrollView>
            <ScrollHint
              visible={!scrolledToBottom}
              onPress={() =>
                expandedListRef.current?.scrollToEnd({animated: true})
              }
            />
          </View>
        ) : (
          playerRows
        )}
        {!expanded && overflowCount > 0 && (
          <TouchableOpacity
            style={styles.viewAllRow}
            onPress={() => setExpanded(true)}
            testID="csoc-identity-view-all-btn">
            <CCText fontSize={13} fontWeight="semibold" color={CCColors.Primary.Default}>
              View all {sortedPlayers.length} players
            </CCText>
          </TouchableOpacity>
        )}
        <PlayerOptionRow
          label="Someone else"
          selected={false}
          onPress={() => {
            setShowFullForm(true);
            onUnresolved();
          }}
        />
      </Box>
    );
  }

  if (!usingFullForm) {
    // Defensive: kept in sync with the branches above via the shared
    // `usingFullForm` boolean, so this should be unreachable -- but if the
    // branches above and this fallthrough ever drift apart again, fail
    // closed (render nothing) rather than silently reaching a form whose
    // resolve/unresolve effect never fires.
    return null;
  }

  return (
    <Box padding={12} gap={8}>
      <View>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
          Student Name
        </CCText>
        <CCTextInput
          testID="csoc-identity-name-input"
          placeholder="Student's full name"
          value={name}
          onChangeText={setName}
        />
      </View>
      <View>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
          Link your email address with CircleChess
        </CCText>
        <CCTextInput
          testID="csoc-identity-email-input"
          placeholder="Email address"
          value={email}
          onChangeText={setEmail}
        />
      </View>
      <View>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
          Date of birth (optional)
        </CCText>
        <CCTextInput
          testID="csoc-identity-dob-input"
          placeholder="DD-MM-YYYY"
          value={dob}
          onChangeText={setDob}
        />
      </View>
      <View>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
          Gender (optional)
        </CCText>
        <CCTextInput
          testID="csoc-identity-gender-input"
          placeholder="Gender"
          value={gender}
          onChangeText={setGender}
        />
      </View>
    </Box>
  );
};

const styles = StyleSheet.create({
  optRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: 'rgba(38, 35, 34, 0.10)',
    borderRadius: 12,
  },
  optRowSelected: {
    borderColor: '#D1AB41',
    backgroundColor: '#FFFDF5',
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: 'rgba(77, 63, 55, 0.25)',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: '#D1AB41',
  },
  radioDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    backgroundColor: '#D1AB41',
  },
  viewAllRow: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  expandedListWrap: {
    position: 'relative',
  },
  expandedList: {
    maxHeight: EXPANDED_LIST_MAX_HEIGHT,
  },
});

export default CsocPlayerIdentityStep;
